// Debug state singleton with pub/sub pattern.
//
// Gates staff-only debug affordances across the app (the top bar's
// window-size indicator, info-drawer event IDs, start-adventure-view's RNG
// seed override, etc.). The backing store is the "debug-info" localStorage
// key so the flag persists across reloads; top-bar.js toggles it on the
// backtick keybind.
//
// Usage:
//
//   import { debugState } from '../state/debug-state.js';
//
//   connectedCallback() {
//     super.connectedCallback();
//     this._debugMode = debugState.enabled;
//     this._unsubscribeDebug = debugState.subscribe(() => {
//       this._debugMode = debugState.enabled;
//     });
//   }
//
//   disconnectedCallback() {
//     super.disconnectedCallback?.();
//     this._unsubscribeDebug?.();
//   }

const STORAGE_KEY = 'debug-info';

class DebugStateManager {
  constructor() {
    /** @type {Set<() => void>} */
    this.listeners = new Set();
    /** @type {boolean} */
    this.enabled = localStorage.getItem(STORAGE_KEY) === 'true';
  }

  /**
   * Subscribe to debug-mode changes. Returns an unsubscribe function.
   * @param {() => void} fn
   * @returns {() => void}
   */
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Notify all subscribers of a change. */
  notify() {
    this.listeners.forEach((fn) => {
      fn();
    });
  }

  /**
   * Set the debug flag explicitly.
   * @param {boolean} value
   */
  set(value) {
    const next = !!value;
    if (next === this.enabled) return;
    this.enabled = next;
    localStorage.setItem(STORAGE_KEY, String(next));
    this.notify();
  }

  /** Flip the debug flag. */
  toggle() {
    this.set(!this.enabled);
  }
}

export const debugState = new DebugStateManager();
